import React, { useState, useEffect } from "react";
import styles from "./imgToWordModal.module.css";
import axios from "axios";
import { addNewLearningTool } from "../../../utilities/learningZonesUtils";

const apiUrl = process.env.REACT_APP_URL;
axios.defaults.baseURL = `${apiUrl}`;
axios.defaults.withCredentials = true;

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const ImgToWordModal = ({
  header,
  unitContent,
  learningToolId,
  learningZoneId,
  approveBtnText,
  cancelBtnText,
  show,
  onClose,
  onConfirm,
}) => {
  /* The image to word modal is used to enter details of a new image to word unit, as well as to edit an existing image to word unit. */

  const [initialState, setInitialState] = useState({
    solutions: [],
    correctSolutionIdx: 0,
    resourceUrl: "",
  });

  const [solutions, setSolutions] = useState([]);
  const [correctSolutionIdx, setCorrectSolutionIdx] = useState(0); // Default to the first solution
  const [resourceUrl, setResourceUrl] = useState("");
  const [useCurrentImage, setUseCurrentImage] = useState(true);
  const [newImageFile, setNewImageFile] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    if (unitContent && show) {
      const correctSolutionIdx = unitContent.unitSolutions.findIndex(
        (solution) => solution.solution_text === unitContent.correct_answer
      );

      const initialState = {
        solutions: unitContent.unitSolutions.map((solution, index) => ({
          solutionText: solution.solution_text,
          solutionNumberInUnit: index + 1,
        })), // Each solution has solutionText and solutionNumberInUnit
        correctSolutionIdx: correctSolutionIdx === -1 ? 0 : correctSolutionIdx, // Default to the first solution if none is found
        resourceUrl: unitContent.resource_url,
      };

      setInitialState(initialState);
      setSolutions(initialState.solutions);
      setCorrectSolutionIdx(initialState.correctSolutionIdx);
      setResourceUrl(initialState.resourceUrl);
      setUseCurrentImage(true);
      setNewImageFile(null);
    } else {
      setSolutions([]);
      setCorrectSolutionIdx(0);
      setResourceUrl("");
      setUseCurrentImage(false);
      setNewImageFile(null);
    }
  }, [unitContent, show]);

  const validateForm = () => {
    if (
      solutions.length >= 2 &&
      correctSolutionIdx !== null &&
      solutions.every((solution) => solution.solutionText.trim() !== "") &&
      (useCurrentImage || newImageFile)
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  };

  useEffect(() => {
    validateForm();
  }, [solutions, correctSolutionIdx, useCurrentImage, newImageFile]);

  /**
   * Handle a solution text change
   * @param {*} index - the index of the solution in the unit
   * @param {*} value - the new value of the solution
   */
  const handleSolutionChange = (index, value) => {
    const newSolutions = [...solutions];
    newSolutions[index].solutionText = value;
    setSolutions(newSolutions);
  };

  /**
   * Add a solution to a unit
   */
  const handleAddSolution = () => {
    setSolutions([
      ...solutions,
      { solutionText: "", solutionNumberInUnit: solutions.length + 1 },
    ]);
  };

  /**
   * Remove a solution from a unit
   * @param {*} index - the solution index in the unit
   */
  const handleRemoveSolution = (index) => {
    const newSolutions = solutions.filter((_, i) => i !== index);
    setSolutions(newSolutions);
    if (correctSolutionIdx === index) {
      setCorrectSolutionIdx(0); // Default to the first solution if the current correct one is removed
    } else if (correctSolutionIdx > index) {
      setCorrectSolutionIdx(correctSolutionIdx - 1);
    }
    // Update solutionNumberInUnit for remaining solutions
    setSolutions(
      newSolutions.map((solution, i) => ({
        ...solution,
        solutionNumberInUnit: i + 1,
      }))
    );
  };

  /**
   * Handle change of an image of a unit, including validation.
   */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setFileError("File size exceeds 2MB. Please upload a smaller file.");
        setNewImageFile(null);
      } else if (!file.type.startsWith("image/")) {
        setFileError("Please upload a valid image file.");
        setNewImageFile(null);
      } else {
        setNewImageFile(file);
        setUseCurrentImage(false);
        setFileError("");
      }
    }
  };

  /**
   * Handle a new unit to the learning tool.
   */
  const handleAddNewUnit = async () => {
    let toolId = learningToolId;

    if (!toolId) {
      /* This is the first unit of a new learning tool, which should be created first. */
      try {
        toolId = await addNewLearningTool(learningZoneId, "imgToWord");
      } catch (error) {
        console.error(error.message);
        return;
      }
    }

    const newUnitData = {
      learningToolId: toolId,
      unitSolutions: solutions,
      correct_solution: solutions[correctSolutionIdx]?.solutionText,
    };

    try {
      const res = await axios.post(
        `/learning_units/create/img_to_word`,
        newUnitData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (res.status === 200 && res.data.success) {
        const learningUnitId = res.data.learning_unit_id;

        if (newImageFile) {
          const formData = new FormData();
          formData.append("image", newImageFile);
          formData.append("learningToolId", toolId);

          const imageRes = await axios.put(
            `/learning_units/add_image/img_to_word/${learningUnitId}`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          if (imageRes.status === 200 && imageRes.data.success) {
            newUnitData.resource_url = imageRes.data.resource_url;
          }
        }

        onConfirm({
          learning_tool_id: toolId,
          tool_type_id: "imgToWord",
          newUnit: {
            learning_unit_id: learningUnitId,
            unitSolutions: solutions.map((solution, index) => ({
              solution_text: solution.solutionText,
              solution_number_in_unit: index + 1,
              is_active: 1,
              learning_unit_id: learningUnitId,
            })),
            correct_answer: solutions[correctSolutionIdx]?.solutionText,
            resource_url: newUnitData.resource_url,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_active: 1,
            learning_tool_id: toolId,
            unit_type: "imgToWord",
          },
        });
      }
    } catch (error) {
      console.error("Error - unit was not added.", error);
    }

    onClose();
  };

  /* Update the content of a unit, after editing is completed. */
  const handleConfirm = async () => {
    const updatedUnitContent = {
      useCurrentImage,
      imageFile: newImageFile,
      unitSolutions: solutions,
      correct_solution: solutions[correctSolutionIdx]?.solutionText,
      resource_url: resourceUrl,
    };

    try {
      const learningUnitId = unitContent.learning_unit_id;
      const reqUrl = `/learning_units/update/img_to_word/${learningToolId}/${learningUnitId}`;

      const formData = new FormData();
      formData.append(
        "unitSolutions",
        JSON.stringify(
          updatedUnitContent.unitSolutions.map((solution) => ({
            solutionText: solution.solutionText,
            solutionNumberInUnit: solution.solutionNumberInUnit,
          }))
        )
      );
      formData.append("correct_solution", updatedUnitContent.correct_solution);

      if (updatedUnitContent.resource_url) {
        formData.append("resource_url", updatedUnitContent.resource_url);
      }

      if (!updatedUnitContent.useCurrentImage && updatedUnitContent.imageFile) {
        formData.append("image", updatedUnitContent.imageFile);
      }

      const res = await axios.put(reqUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status === 200 && res.data.success) {
        onConfirm({
          learning_unit_id: learningUnitId,
          unitSolutions: updatedUnitContent.unitSolutions.map(
            (solution, index) => ({
              solution_text: solution.solutionText,
              solution_number_in_unit: index + 1,
              is_active: 1,
              learning_unit_id: learningUnitId,
            })
          ),
          correct_answer: updatedUnitContent.correct_solution,
          resource_url: res.data.resource_url || resourceUrl,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          is_active: 1,
          learning_tool_id: learningToolId,
          unit_type: "imgToWord",
        });
      } else {
        console.error("Error - unit was not updated.");
      }
    } catch (error) {
      console.error("Error - unit was not updated.", error);
    }

    onClose();
  };

  /* Reset the modal state, text fields, and error message. */
  const resetState = () => {
    setSolutions(initialState.solutions);
    setCorrectSolutionIdx(initialState.correctSolutionIdx);
    setResourceUrl(initialState.resourceUrl);
    setUseCurrentImage(false);
    setNewImageFile(null);
    setFileError("");
  };

  /* Close the modal when clicking outside it. */
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      resetState();
      onClose();
    }
  };

  /* Close the modal */
  const handleClose = () => {
    resetState();
    onClose();
  };

  /* Handle clicking the cancel button */
  const handleCancel = () => {
    resetState();
    onClose();
  };

  if (!show) {
    return null;
  }

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={handleClose}>
          &times;
        </button>
        <h2>{header}</h2>
        <div className={styles.field}>
          <label>Image</label>
          {unitContent ? (
            <>
              <div className={styles.imageOption}>
                <input
                  type="radio"
                  checked={useCurrentImage}
                  onChange={() => setUseCurrentImage(true)}
                />
                <span>Use current image</span>
              </div>
              <div className={styles.imageOption}>
                <input
                  type="radio"
                  checked={!useCurrentImage}
                  onChange={() => setUseCurrentImage(false)}
                />
                <span>Upload new image</span>
                {!useCurrentImage && (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                )}
              </div>
            </>
          ) : (
            <div className={styles.imageOption}>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
            </div>
          )}
        </div>
        {fileError && <p className={styles.error}>{fileError}</p>}
        <div className={styles.field}>
          <label>Solutions</label>
          <div className={styles.solutionsHeader}>
            <span>Solution Text</span>
            <span style={{ marginLeft: "auto" }}>Correct Solution</span>
          </div>
          {solutions.map((solution, index) => (
            <div key={index} className={styles.solutionField}>
              <input
                type="text"
                value={solution.solutionText || ""}
                onChange={(e) => handleSolutionChange(index, e.target.value)}
              />
              <button onClick={() => handleRemoveSolution(index)}>
                Remove
              </button>
              <input
                type="radio"
                name="correctSolution"
                checked={correctSolutionIdx === index}
                onChange={() => setCorrectSolutionIdx(index)}
                className={styles.correctSolutionRadio}
              />
            </div>
          ))}
          <button onClick={handleAddSolution}>Add Solution</button>
        </div>
        <div className={styles.actions}>
          <button onClick={handleCancel}>{cancelBtnText}</button>
          {unitContent ? (
            <button onClick={handleConfirm} disabled={!isValid}>
              {approveBtnText}
            </button>
          ) : (
            <button onClick={handleAddNewUnit} disabled={!isValid}>
              {approveBtnText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImgToWordModal;
